import React, { useMemo, useState } from "react";
import { Box, Grid, Paper, Typography, Chip, Stack, LinearProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import SQLLayout from "../layout/SQLLayout";
import ObjectExplorer from "../sidebar/ObjectExplorer";
import { databaseAPI } from "../../services/api";

export default function Dashboard() {
  const [sidebarSearch, setSidebarSearch] = useState("");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["databaseInfo"],
    queryFn: () => databaseAPI.getAllTables().then((r) => r.data),
    refetchOnWindowFocus: false,
  });

  const tables = data?.tables ?? [];
  const count = data?.count ?? tables.length ?? 0;

  const stats = useMemo(() => {
    const schemas = new Set(tables.map((t) => t.schema || "dbo"));
    return { schemaCount: schemas.size };
  }, [tables]);

  const onRefresh = async () => {
    await refetch();
    toast.success("Refreshed");
  };

  const onBackup = async () => {
    try {
      const res = await databaseAPI.startBackup();
      const file = res.data?.file;
      if (!file) throw new Error("No backup file returned");
      const url = databaseAPI.getBackupDownloadUrl(file);
      window.location.href = url;
      toast.success("Backup created");
    } catch (e) {
      toast.error("Backup failed (backend required)");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography color="error">Error: {error.message}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <SQLLayout
      title={`${data?.database ?? "Database"} — Explorer`}
      subtitle={`Server: ${data?.server ?? "-"} | Tables: ${count}`}
      onRefresh={onRefresh}
      onBackup={onBackup}
      sidebarSearch={sidebarSearch}
      setSidebarSearch={setSidebarSearch}
      sidebar={<ObjectExplorer tables={tables} filter={sidebarSearch} />}
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Total Tables
            </Typography>
            <Typography variant="h4">{count}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Schemas
            </Typography>
            <Typography variant="h4">{stats.schemaCount}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Quick Actions
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Chip label="Refresh" onClick={onRefresh} clickable />
              <Chip label="Backup" onClick={onBackup} clickable />
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6">SSMS-style navigation</Typography>
            <Typography variant="body2" color="text.secondary">
              Use the Object Explorer on the left to browse schemas/tables like SQL Server Management Studio.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </SQLLayout>
  );
}
